import React, { useState } from "react";
import { useTheme, THEMES } from "../../contexts/ThemeContext";
import { useI18n } from "../../contexts/I18nContext";
import { Palette, Check, X } from "lucide-react";

/**
 * Small pill-style theme picker. Renders a swatch trigger; on click opens a sheet
 * with all themes. CSS variables flip instantly via ThemeContext.
 */
export default function ThemeSwitcher() {
  const { theme, setTheme } = useTheme();
  const { lang } = useI18n();
  const ar = lang === "ar";
  const [open, setOpen] = useState(false);

  const current = THEMES.find((t) => t.key === theme) || THEMES[0];

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="h-10 w-10 rounded-full bg-white/5 border border-[#D4AF37]/30 flex items-center justify-center text-[#D4AF37] transition hover:bg-white/10"
        aria-label={ar ? "تغيير الثيم" : "Change theme"}
        data-testid="theme-switcher-btn"
      >
        <Palette className="h-4 w-4" />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center"
          style={{ background: "rgba(0,0,0,0.7)" }}
          onClick={() => setOpen(false)}
          data-testid="theme-switcher-sheet"
        >
          <div
            className="w-full max-w-[448px] rounded-t-3xl p-5 mobile-dashboard border-t border-[#D4AF37]/30"
            onClick={(e) => e.stopPropagation()}
            dir={ar ? "rtl" : "ltr"}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-black gold-text">{ar ? "اختر ثيم التطبيق" : "Pick your theme"}</h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="h-8 w-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/70"
                aria-label={ar ? "إغلاق" : "Close"}
                data-testid="theme-sheet-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <p className="text-[11px] text-white/55 mb-4">
              {ar
                ? "كل ثيم يستخدم لونين أساسيين متناغمين لمظهر فاخر. التغيير فوري."
                : "Each theme blends two primary tones for a luxe look. Changes are instant."}
            </p>

            <div className="grid grid-cols-2 gap-2.5">
              {THEMES.map((t) => {
                const active = t.key === theme;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => { setTheme(t.key); setOpen(false); }}
                    className={
                      "relative rounded-2xl p-3 flex flex-col items-start gap-2 transition hover:-translate-y-0.5 " +
                      (active ? "border-2 border-[#D4AF37]" : "border border-white/10 hover:border-[#D4AF37]/50")
                    }
                    style={{ background: t.vars["--bg-2"] }}
                    data-testid={`theme-opt-${t.key}`}
                  >
                    <div className="flex items-center gap-1">
                      {t.swatches.map((c, i) => (
                        <span key={i} className="h-5 w-5 rounded-full border border-black/30" style={{ background: c }} aria-hidden />
                      ))}
                    </div>
                    <div className="text-[12px] font-bold" style={{ color: t.vars["--text"] }}>
                      {ar ? t.ar : t.en}
                    </div>
                    {active && (
                      <span className="absolute top-2 end-2 h-5 w-5 rounded-full bg-[#D4AF37] text-black flex items-center justify-center">
                        <Check className="h-3 w-3" />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="text-[10px] text-white/40 text-center mt-4">
              {ar ? `الثيم الحالي: ${current.ar}` : `Current: ${current.en}`}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
